/**
 * Hook for reading, clearing and exporting dose-calculation history.
 *
 * Pi-backed (namespace `stock-history`, shared with the imperative
 * writer in `lib/stock-solutions-log.ts`) via `useServerBackedState`.
 * The legacy localStorage trail (`reefapp.stockSolutions.history`)
 * seeds the default so history shows instantly, even offline.
 */
import { useCallback, useMemo, useState } from 'react';

import { useServerBackedState } from '../hooks/useServerBackedState';
import { STOCK_HISTORY_NAMESPACE } from '../lib/stock-solutions-log';
import type { StockSolutionLogEntry } from './historyTypes';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_HISTORY_KEY = 'reefapp.stockSolutions.history';

function readLegacyHistory(): StockSolutionLogEntry[] {
  try {
    const raw = window.localStorage.getItem(LEGACY_HISTORY_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed as StockSolutionLogEntry[];
    }
    return [];
  } catch {
    return [];
  }
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function entryToCsvRow(entry: StockSolutionLogEntry): string {
  return [
    escapeCsvField(entry.ts),
    escapeCsvField(entry.slug),
    escapeCsvField(entry.mode),
    escapeCsvField(entry.tankGallons),
    String(entry.doseML),
    escapeCsvField(entry.currentLevel ?? ''),
    escapeCsvField(entry.targetLevel ?? ''),
    escapeCsvField(entry.units ?? ''),
    escapeCsvField(entry.dailyConsumption ?? ''),
  ].join(',');
}

const CSV_HEADER =
  'timestamp,slug,mode,tankGallons,doseML,currentLevel,targetLevel,units,dailyConsumption';

export interface UseStockSolutionsHistoryResult {
  history: StockSolutionLogEntry[];
  clear(): void;
  toCSV(): string;
}

export function useStockSolutionsHistory(): UseStockSolutionsHistoryResult {
  const [legacyDefault] = useState(readLegacyHistory);
  const { value, setValue } = useServerBackedState<StockSolutionLogEntry[]>(
    STOCK_HISTORY_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob.
  const history = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  const clear = useCallback(() => {
    // Clear the Pi copy (+ its mirror) and the legacy trail so the
    // export truly empties — a user-initiated clear, not a deletion of
    // migration seeds.
    setValue([]);
    try {
      window.localStorage.setItem(LEGACY_HISTORY_KEY, JSON.stringify([]));
    } catch {
      // storage full — non-fatal
    }
  }, [setValue]);

  const toCSV = useCallback((): string => {
    const rows = history.map(entryToCsvRow);
    return [CSV_HEADER, ...rows].join('\n');
  }, [history]);

  return useMemo(() => ({ history, clear, toCSV }), [history, clear, toCSV]);
}
